import api from './api';
import { Tarefa, TempoResponsavel } from '../types';

// Interface para os filtros de listagem de tarefas
export interface TarefaFiltros {
  status?: string;
  prioridade?: string;
  responsavel?: string;
  criador?: string; // Filtrar por criador
  departamento?: string;
  pagina?: number;
  limite?: number;
  arquivadas?: boolean;
  dataInicio?: string;
  dataFim?: string;
  busca?: string; // Busca por título ou descrição
  // Parâmetros para otimização
  carregarConcluidas?: boolean;
  diasConcluidas?: number;
  // Novos parâmetros para visualização de departamento
  tarefasDepartamento?: boolean; // Indica se deve mostrar todas as tarefas do departamento (para líderes)
  tipo?: 'recebidas' | 'enviadas'; // Tipo de tarefas a serem exibidas
  numero?: string; // Últimos caracteres do ID (ex: "8716" para #8716)
}

// Interface para a resposta paginada
export interface RespostaPaginada<T> {
  success: boolean;
  tarefas: T[];
  total: number;
  pagina: number;
  totalPaginas: number;
}

export const tarefaService = {
  /**
   * Contador de tarefas não concluídas (para notificação ao login)
   */
  contarTarefasPendentes: async (): Promise<number> => {
    try {
      const response = await api.get('/tarefas/count-pendentes');
      return response.data.count || 0;
    } catch (error) {
      console.error('Erro ao contar tarefas pendentes:', error);
      return 0;
    }
  },

  /**
   * Listar tarefas recebidas (onde usuário é responsável)
   * SEM paginação - retorna TODAS as tarefas
   */
  listarTarefasRecebidas: async (): Promise<Tarefa[]> => {
    try {
      const response = await api.get('/tarefas/recebidas');
      return response.data.tarefas || [];
    } catch (error) {
      console.error('Erro ao listar tarefas recebidas:', error);
      throw error;
    }
  },

  /**
   * Listar tarefas enviadas (onde usuário é criador)
   * SEM paginação - retorna TODAS as tarefas
   */
  listarTarefasEnviadas: async (): Promise<Tarefa[]> => {
    try {
      const response = await api.get('/tarefas/enviadas');
      return response.data.tarefas || [];
    } catch (error) {
      console.error('Erro ao listar tarefas enviadas:', error);
      throw error;
    }
  },

  /**
   * Busca todas as tarefas com filtros opcionais e suporte a paginação
   */
  listarTarefas: async (filtros?: TarefaFiltros) => {
    try {
      const params = new URLSearchParams();
      if (filtros) {
        // Remover filtros que já são valores padrão na API
        const { diasConcluidas, carregarConcluidas, ...outrosFiltros } = filtros;
        
        // Só adicionar diasConcluidas se for diferente do padrão (2)
        if (diasConcluidas !== undefined && diasConcluidas !== 2) {
          params.append('diasConcluidas', String(diasConcluidas));
        }
        
        // Só adicionar carregarConcluidas se for diferente do padrão (true)
        if (carregarConcluidas !== undefined && carregarConcluidas !== true) {
          params.append('carregarConcluidas', String(carregarConcluidas));
        }
        
        // Adicionar os demais filtros
        Object.entries(outrosFiltros).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
      }
      
      // Adicionar timestamp para evitar cache do navegador
      params.append('_t', Date.now().toString());
      
      const response = await api.get(`/tarefas?${params.toString()}`);
      return response.data as RespostaPaginada<Tarefa>;
    } catch (error) {
      console.error('Erro ao listar tarefas:', error);
      throw error;
    }
  },
  
  /**
   * Busca tarefas arquivadas (histórico)
   */
  listarTarefasArquivadas: async (filtros?: Omit<TarefaFiltros, 'arquivadas'>) => {
    try {
      return await tarefaService.listarTarefas({
        ...filtros,
        arquivadas: true,
        pagina: filtros?.pagina || 1,
        limite: filtros?.limite || 20
      });
    } catch (error) {
      console.error('Erro ao listar tarefas arquivadas:', error);
      throw error;
    }
  },

  /**
   * Busca tarefas concluídas (histórico)
   */
  listarTarefasConcluidas: async (filtros?: TarefaFiltros) => {
    try {
      return await tarefaService.listarTarefas({
        ...filtros,
        status: 'Concluída',
        pagina: filtros?.pagina || 1,
        limite: filtros?.limite || 20,
        carregarConcluidas: true,
        // Por padrão, carregamos tarefas concluídas nos últimos 30 dias
        diasConcluidas: filtros?.diasConcluidas || 30
      });
    } catch (error) {
      console.error('Erro ao listar tarefas concluídas:', error);
      throw error;
    }
  },
  
  /**
   * Arquiva ou desarquiva uma tarefa
   * @returns {Object} Objeto com status de sucesso e dados da resposta
   */
  arquivarTarefa: async (id: string, arquivada: boolean) => {
    try {
      const response = await api.patch(`/tarefas/${id}/arquivar`, { arquivada });
      return { 
        success: true, 
        data: response.data,
        message: `Tarefa ${arquivada ? 'arquivada' : 'desarquivada'} com sucesso.`
      };
    } catch (error: any) {
      console.error(`Erro ao ${arquivada ? 'arquivar' : 'desarquivar'} tarefa ${id}:`, error);
      
      // Verificar se o erro é de constraint violation (já esperado)
      const isConstraintViolation = error?.response?.data?.error?.includes('violates check constraint');
      
      if (isConstraintViolation) {
        console.log('Erro de constraint violation detectado, mas a tarefa foi atualizada no banco');
        // Retornamos sucesso mesmo com erro, pois a tarefa foi atualizada no banco
        return { 
          success: true, 
          data: null,
          message: `Tarefa ${arquivada ? 'arquivada' : 'desarquivada'} com sucesso, mas houve um erro ao registrar a atividade.`
        };
      }
      
      // Para outros tipos de erro, retornamos falha
      return {
        success: false,
        data: null,
        error: error,
        message: `Erro ao ${arquivada ? 'arquivar' : 'desarquivar'} tarefa.`
      };
    }
  },

  /**
   * Busca uma tarefa específica pelo ID
   */
  obterTarefa: async (id: string) => {
    try {
      const response = await api.get(`/tarefas/${id}`);
      // A API retorna { success: true, tarefa: {...} }
      // Verificamos se a resposta tem o formato esperado
      if (response.data && response.data.success && response.data.tarefa) {
        return response.data.tarefa;
      } else if (response.data) {
        // Se a resposta não tem o formato esperado, mas tem dados, retornamos os dados
        return response.data;
      }
      throw new Error('Formato de resposta inválido');
    } catch (error) {
      console.error(`Erro ao obter tarefa ${id}:`, error);
      throw error;
    }
  },

  /**
   * Cria uma nova tarefa
   */
  criarTarefa: async (tarefa: Partial<Tarefa>) => {
    try {
      const response = await api.post('/tarefas', tarefa);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar tarefa:', error);
      throw error;
    }
  },

  /**
   * Atualiza uma tarefa existente
   */
  atualizarTarefa: async (id: string, dados: Partial<Tarefa> & { responsavel_id?: string }) => {
    try {
      // Preparar os dados para envio à API
      // Garantir que temos o responsavel_id correto
      const dadosAPI: any = { ...dados };
      
      // Se temos um objeto responsavel, mas não temos responsavel_id explícito
      if (dados.responsavel && !dadosAPI.responsavel_id) {
        dadosAPI.responsavel_id = dados.responsavel.id;
      }

      // O backend espera snake_case para estes dois campos — sem esse
      // mapeamento, dataVencimento/dataNecessidade nunca chegavam à API
      // (o body ia com a chave camelCase, que a rota do backend ignora)
      if (dadosAPI.dataVencimento !== undefined) {
        dadosAPI.data_vencimento = dadosAPI.dataVencimento;
        delete dadosAPI.dataVencimento;
      }
      if (dadosAPI.dataNecessidade !== undefined) {
        dadosAPI.data_necessidade = dadosAPI.dataNecessidade;
        delete dadosAPI.dataNecessidade;
      }

      // IMPORTANTE: Remover campos bloqueados que não podem ser editados após criação
      // Título, descrição e prioridade são imutáveis após a criação da tarefa
      delete dadosAPI.titulo;
      delete dadosAPI.descricao;
      delete dadosAPI.prioridade;
      
      // Remover também campos que não devem ser enviados na atualização
      delete dadosAPI.id;
      delete dadosAPI.responsavel;
      delete dadosAPI.criador;
      delete dadosAPI.departamento;
      delete dadosAPI.dataCriacao;
      delete dadosAPI.dataAtualizacao;
      delete dadosAPI.anexos;
      delete dadosAPI.comentarios;
      delete dadosAPI.atividades;
      
      console.log('Enviando dados para API (campos permitidos):', dadosAPI);
      const response = await api.put(`/tarefas/${id}`, dadosAPI);
      return response.data;
    } catch (error) {
      console.error(`Erro ao atualizar tarefa ${id}:`, error);
      throw error;
    }
  },

  /**
   * Atualiza apenas o status de uma tarefa (usado no arrastar do Kanban)
   */
  atualizarStatusTarefa: async (id: string, novoStatus: string) => {
    try {
      const response = await api.put(`/tarefas/${id}`, { status: novoStatus });
      return response.data;
    } catch (error) {
      console.error(`Erro ao atualizar status da tarefa ${id}:`, error);
      throw error;
    }
  },

  /**
   * Exclui uma tarefa
   */
  excluirTarefa: async (id: string) => {
    try {
      const response = await api.delete(`/tarefas/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao excluir tarefa ${id}:`, error);
      throw error;
    }
  },

  /**
   * Adiciona um comentário a uma tarefa
   */
  adicionarComentario: async (tarefaId: string, texto: string) => {
    try {
      const response = await api.post(`/tarefas/${tarefaId}/comentarios`, { texto });
      return response.data;
    } catch (error) {
      console.error(`Erro ao adicionar comentário à tarefa ${tarefaId}:`, error);
      throw error;
    }
  },

  /**
   * NOVA: Busca avançada de tarefas com filtros completos
   * Disponível para todos os usuários com permissões específicas:
   * - Comum: apenas suas tarefas
   * - Líder: suas tarefas + pode filtrar por departamento
   * - Admin: todas as tarefas
   */
  obterTempoResponsavel: async (tarefaId: string): Promise<TempoResponsavel[]> => {
    try {
      const response = await api.get(`/tarefas/${tarefaId}/tempo`);
      return (response.data.historico || []).map((item: Record<string, unknown>) => ({
        id: item.id,
        tarefaId: item.tarefa_id,
        responsavelId: item.responsavel_id,
        responsavelNome: item.responsavel_nome,
        inicio: item.inicio,
        fim: item.fim,
        duracaoSegundos: item.duracao_segundos,
        emAndamento: item.em_andamento,
      }));
    } catch (error) {
      console.error(`Erro ao buscar tempo de responsável para tarefa ${tarefaId}:`, error);
      return [];
    }
  },

  buscarTarefas: async (filtros?: TarefaFiltros & { incluirConcluidas?: boolean }) => {
    try {
      const params = new URLSearchParams();
      if (filtros) {
        Object.entries(filtros).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
      }
      
      // Adicionar timestamp para evitar cache
      params.append('_t', Date.now().toString());
      
      const response = await api.get(`/tarefas/buscar?${params.toString()}`);
      return response.data as RespostaPaginada<Tarefa>;
    } catch (error) {
      console.error('Erro na busca avançada de tarefas:', error);
      throw error;
    }
  }
};

export default tarefaService;
